// coachingMentions.ts — May 7, 2026
//
// Encoding: a mention is stored inline in the message body as
//   @[Full Name](uuid)
// where uuid is the team_roster.id of the tagged user. This token is the
// canonical form — it survives copy/paste, round-trips through the DB unchanged,
// and gives the renderer everything it needs to draw a styled chip without a
// second lookup.
//
// Why inline (vs. a separate spans table): keeps the data model trivial —
// one TEXT column. The mentions[] uuid column on coaching_messages is
// derived from the body and exists purely to power "messages mentioning me"
// queries via GIN. Truth source is the body.

import type { ReactNode } from "react";
import { createElement, Fragment } from "react";

// Matches @[Full Name](uuid). Names can contain anything except `]` and `\n`.
// UUID is the standard 8-4-4-4-12 hex shape — strict so we don't false-match
// stray markdown links the user might paste.
const MENTION_RE =
  /@\[([^\]\n]+)\]\(([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\)/g;

export interface ParsedMention {
  name: string;
  user_id: string;
  /** Character offset in the original body where this mention starts. */
  start: number;
  /** Character offset (exclusive) where this mention ends. */
  end: number;
}

/**
 * Extract every @[Name](uuid) mention from a body string.
 * Order is left-to-right; duplicates are preserved (caller dedupes if needed).
 */
export function parseMentions(body: string): ParsedMention[] {
  if (!body) return [];
  const out: ParsedMention[] = [];
  // String.matchAll works in all evergreen browsers and our build target (ES2020).
  for (const m of body.matchAll(MENTION_RE)) {
    out.push({
      name: m[1],
      user_id: m[2],
      start: m.index ?? 0,
      end: (m.index ?? 0) + m[0].length,
    });
  }
  return out;
}

/**
 * Return just the unique uuids mentioned in a body. Order-preserving.
 * Use this to populate coaching_messages.mentions and to fan out notifications.
 */
export function mentionUserIds(body: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of parseMentions(body)) {
    if (!seen.has(m.user_id)) {
      seen.add(m.user_id);
      out.push(m.user_id);
    }
  }
  return out;
}

/**
 * Convert a body with @-mention tokens into a plain-text version where each
 * mention is rendered as `@Full Name`. Useful for SMS, notification preview
 * text, and anywhere chips can't render.
 */
export function stripMentions(body: string): string {
  if (!body) return "";
  return body.replace(MENTION_RE, "@$1");
}

/**
 * Render a body string as React nodes, replacing each @[Name](uuid) token
 * with a styled `<span>` chip. Plain text in between renders as-is, with
 * newlines preserved (we don't \n→<br/> here — call sites use whitespace-pre-wrap
 * which already handles that).
 */
export function renderMessageBody(body: string): ReactNode {
  if (!body) return null;
  const parsed = parseMentions(body);
  if (parsed.length === 0) return body;

  const nodes: ReactNode[] = [];
  let cursor = 0;
  parsed.forEach((m, i) => {
    if (m.start > cursor) {
      nodes.push(body.slice(cursor, m.start));
    }
    nodes.push(
      createElement(
        "span",
        {
          key: `m-${i}-${m.user_id}`,
          className:
            "inline-block px-1.5 py-0.5 rounded font-semibold text-bardin-teal bg-teal-50 border border-teal-100",
        },
        `@${m.name}`,
      ),
    );
    cursor = m.end;
  });
  if (cursor < body.length) {
    nodes.push(body.slice(cursor));
  }
  return createElement(Fragment, null, ...nodes);
}

/**
 * Build the canonical inline token for a mention. Centralized so the
 * MentionInput and any other producers stay consistent.
 */
export function formatMentionToken(name: string, user_id: string): string {
  // Strip ] and newlines from the display name — these are the only chars
  // that would break the regex round-trip. Won't appear in real names.
  const safeName = name.replace(/[\]\n]/g, "").trim() || "user";
  return `@[${safeName}](${user_id})`;
}
